import React, { useState, useEffect, useCallback } from "react";
import { jobService } from "../../services/job.service.js";
import { useParams } from "react-router-dom";
import { resumeApi } from "../../services/resumeApi.js";
import InviteModal from '../../components/common/modal/InviteModal.jsx';
import { toast } from 'react-toastify';
import ResumesContent from "../../components/common/table/ResumesTable.jsx";
import ResendConfirmationModal from '../../components/common/modal/ResendConfirmationModal.jsx';
import PassingScoreModal from "../../components/common/modal/PassingScoreModal.jsx";
import LinkExpiryModal from '../../components/common/modal/LinkExpiryModal.jsx';
import FeedbackModal from "../../components/common/modal/FeedbackModal.jsx";
import config from '../../config/config.js';

const FinalInterviewPage = () => {
  const { id } = useParams();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [shortlistedResumes, setShortlistedResumes] = useState([]);
  const [totalResumes, setTotalResumes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showReportView, setShowReportView] = useState(true);
  // Default 'shortlisted' to match the other stage tabs — consistent counts (ClickUp #86d36ebxh).
  const [currentFilter, setCurrentFilter] = useState('shortlisted');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [error, setError] = useState(null);
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [candidateToResend, setCandidateToResend] = useState(null);
  const [selectedResumeForScore, setSelectedResumeForScore] = useState(null);
  const [showPassingScoreModal, setShowPassingScoreModal] = useState(false);
  const [jobDetails, setJobDetails] = useState(null);
  const [showLinkExpiryModal, setShowLinkExpiryModal] = useState(false);
  const [candidateToInvite, setCandidateToInvite] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isLinkExpiryLoading, setIsLinkExpiryLoading] = useState(false);

  const checkExistingInvites = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await resumeApi.getAllResumes(id, 1, itemsPerPage);
      setShortlistedResumes(response.data.resumes);
      setTotalResumes(response.data.total);
    } catch (error) {
      console.error("Error checking status:", error);
      toast.error("Failed to load resume data");
    } finally {
      setIsLoading(false);
    }
  }, [id, itemsPerPage]);

  const fetchJobDetails = useCallback(async () => {
    try {
      const response = await jobService.getJob(id);
      setJobDetails(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to fetch job details');
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      checkExistingInvites();
      fetchJobDetails();
    }
  }, [id, checkExistingInvites, fetchJobDetails]);

  const fetchShortlistedResumes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await resumeApi.getAllResumes(
        id,
        currentPage,
        itemsPerPage,
        currentFilter
      );
      setShortlistedResumes(response.data.resumes);
      setTotalResumes(response.data.total);
    } catch (error) {
      console.error("Error fetching resumes:", error);
      toast.error("Failed to load resume data");
    } finally {
      setIsLoading(false);
    }
  }, [id, currentPage, itemsPerPage, currentFilter]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchShortlistedResumes();
  };

  useEffect(() => {
    fetchShortlistedResumes();
  }, [fetchShortlistedResumes]);

  useEffect(() => {
    fetchShortlistedResumes();
  }, [currentPage, itemsPerPage, fetchShortlistedResumes]);

  // ── Real-time score polling ──────────────────────────────────────────
  // Poll every 30 s while there are candidates with interviews in progress
  // (invite sent but not yet scored). Stops automatically when none remain.
  useEffect(() => {
    const hasPending = shortlistedResumes.some(
      r => r.inviteStatus?.finalInterview === "sent"
    );
    if (!hasPending) return;
    const timer = setInterval(() => { fetchShortlistedResumes(); }, 15000);
    return () => { clearInterval(timer); fetchShortlistedResumes(); };
  }, [shortlistedResumes, fetchShortlistedResumes]);

  const handleDelete = async (resumeId) => {
    try {
      await resumeApi.deleteResume(resumeId);
      fetchShortlistedResumes();
    } catch (error) {
      console.error("Error deleting resume:", error);
      setError("Failed to delete resume");
    }
  };

  const handleSendInvitation = () => {
    const eligibleResumes = shortlistedResumes
      .filter(resume => 
        resume.psychoanalyticalStatus === 'shortlisted' && 
        (!resume.inviteStatus?.finalInterview || 
         resume.inviteStatus.finalInterview === 'pending' || 
         resume.inviteStatus.finalInterview === null)
      )
      .map(resume => ({
        email: resume.email,
        name: resume.candidateName,
        id: resume._id
      }));

    if (eligibleResumes.length === 0) {
      toast.warning('No eligible candidates found for sending invites');
      return;
    }

    setSelectedEmails(eligibleResumes);
    setShowInviteModal(true);
  };

  const handleSendInvites = async (selectedCandidates, expiryDate) => {
    try {
      setIsLoading(true);
      if (!id) {
        toast.error('Job ID is missing');
        return;
      }
      const invitePromises = selectedCandidates.map(async (candidate) => {
        const inviteData = {
          type: 'finalInterview',
          link: `${config.baseUrl}/final-interview/${id}/${candidate.id}`,
          jobId: id,
          linkExpiry: expiryDate
        };
        await resumeApi.sendInvite(candidate.id, inviteData);
      });
      await Promise.all(invitePromises);
      setShortlistedResumes(prevResumes =>
        prevResumes.map(resume => {
          if (selectedCandidates.some(selected => selected.id === resume._id)) {
            return {
              ...resume,
              inviteStatus: {
                ...resume.inviteStatus,
                finalInterview: 'sent'
              },
              inviteSentAt: {
                ...resume.inviteSentAt,
                finalInterview: new Date()
              }
            };
          }
          return resume;
        })
      );
      setShowInviteModal(false);
      toast.success(`Invites sent successfully to ${selectedCandidates.length} candidates`);
      setShowReportView(true);
    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error('Failed to send invites. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteIndividual = (resume) => {
    setCandidateToInvite(resume);
    setShowLinkExpiryModal(true);
  };

  const handleExpiryConfirm = async (expiryDate) => {
    if (candidateToInvite) {
      try {
        setIsLinkExpiryLoading(true);
        const inviteData = {
          type: 'finalInterview',
          link: `${config.baseUrl}/final-interview/${id}/${candidateToInvite._id}`,
          jobId: id,
          linkExpiry: expiryDate
        };
        await resumeApi.sendInvite(candidateToInvite._id, inviteData);
        toast.success('Invite sent successfully');
      } catch (error) {
        console.error('Error sending invite:', error);
        toast.error('Failed to send invite');
      } finally {
        setIsLinkExpiryLoading(false);
        setCandidateToInvite(null);
        setShowLinkExpiryModal(false);
        fetchShortlistedResumes();
      }
    }
  };

  const handleViewFeedback = (resume) => {
    setSelectedFeedback({
      score: resume.finalInterviewResult?.overallScore || 0,
      feedback: resume.finalInterviewResult?.detailedAnalysis || ''
    });
    setShowFeedbackModal(true);
  };

  const handleStatusChange = async (resumeId, type, newStatus, override = false) => {
    try {
      await resumeApi.updateResumeStatus(resumeId, type, newStatus, override);
      setShortlistedResumes(prev =>
        prev.map(r => r._id === resumeId
          ? { ...r, [`${type}Status`]: newStatus }
          : r
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleScoreUpdate = async (resumeId, score, type) => {
    try {
      // Update the local state immediately for better UX
      setShortlistedResumes(prevResumes => 
        prevResumes.map(resume => {
          if (resume._id === resumeId) {
            return {
              ...resume,
              finalInterviewScore: score,
              finalInterviewStatus: score >= 70 ? 'shortlisted' : 'rejected'
            };
          }
          return resume;
        })
      );
      
      // Refresh data from server to ensure consistency
      await fetchShortlistedResumes();
    } catch (error) {
      console.error('Error updating score:', error);
      toast.error('Failed to update score');
    }
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="hidden">
          showInviteModal: {String(showInviteModal)},
          selectedEmails: {JSON.stringify(selectedEmails)}
        </div>
      )}

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
        }}
        emails={selectedEmails || []}
        testType="finalInterview"
        onSend={handleSendInvites}
      />

      <LinkExpiryModal
        isOpen={showLinkExpiryModal}
        onClose={() => {
          setShowLinkExpiryModal(false);
          setCandidateToInvite(null);
        }}
        onConfirm={handleExpiryConfirm}
        testType="finalInterview"
        isLoading={isLinkExpiryLoading}
      />

      {isLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {showReportView ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 font-medium">
                    FI
                  </div>
                  <h2 className="ml-4 text-xl font-semibold">Final Interview</h2>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleSendInvitation}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    Send Invitations
                  </button>
                </div>
              </div>
              <ResumesContent
                resumes={shortlistedResumes}
                pagination={{
                  page: currentPage,
                  totalPages: Math.ceil(totalResumes / itemsPerPage),
                  total: totalResumes,
                  limit: itemsPerPage
                }}
                setPagination={(newPagination) => {
                  setCurrentPage(newPagination.page);
                  setItemsPerPage(newPagination.limit);
                }}
                onPageChange={handlePageChange}
                currentFilter={currentFilter}
                screenType="finalInterview"
                onInvite={handleInviteIndividual}
                showDelete={false}
                jobDetails={jobDetails}
                onViewFeedback={handleViewFeedback}
                onRefresh={fetchShortlistedResumes}
                onScoreUpdate={handleScoreUpdate}
                onStatusChange={handleStatusChange}
                loading={isLoading}
              />
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-medium">No candidates found</p>
              <p className="text-sm text-gray-400">
                Complete previous stages to see candidates here
              </p>
            </div>
          )}
        </>
      )}

      <ResendConfirmationModal
        isOpen={showResendConfirmModal}
        onClose={() => {
          setShowResendConfirmModal(false);
          setCandidateToResend(null);
        }}
        onConfirm={async () => {
          if (candidateToResend) {
            setCandidateToInvite(candidateToResend);
            setShowResendConfirmModal(false);
            setCandidateToResend(null);
            setShowLinkExpiryModal(true);
          }
        }}
        candidateEmail={candidateToResend?.email}
      />

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setSelectedFeedback(null);
        }}
        score={selectedFeedback?.score}
        feedback={selectedFeedback?.feedback}
      />
    </div>
  );
};

export default FinalInterviewPage;






